'use client';

import { useState, useCallback, useMemo } from 'react';
import Link from 'next/link';
import {
  Cpu,
  Database,
  Server,
  VectorSquare,
  Workflow,
  Zap,
} from 'lucide-react';
import { motion } from 'framer-motion';

// Extracted AI Card Component
const AICard = ({
  title,
  description,
  technologies,
  achievements,
  icon,
  gradientFrom,
  gradientTo,
  hoverGradientFrom,
  hoverGradientTo,
  index,
}: {
  title: string;
  description: string;
  technologies: string[];
  achievements: string[];
  icon: React.ReactNode;
  gradientFrom: string;
  gradientTo: string;
  hoverGradientFrom: string;
  hoverGradientTo: string;
  index: number;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{
        duration: 0.6,
        delay: index * 0.1,
        type: 'spring',
        stiffness: 100,
      }}
      whileHover={{
        y: -8,
        scale: 1.02,
        transition: { duration: 0.3, type: 'spring', stiffness: 300 },
      }}
      className="group relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        className="bg-card/80 border-border/50 group-hover:border-primary/30 h-full rounded-2xl border p-6 transition-all duration-500 hover:shadow-2xl"
        whileHover={{
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          borderColor: 'rgba(139, 92, 246, 0.3)',
        }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          className="mb-6 flex items-center gap-4"
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
        >
          <motion.div
            className={`relative flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl border-2 border-white/30 bg-gradient-to-br ${gradientFrom} ${gradientTo} shadow-xl backdrop-blur-sm`}
            whileHover={{
              scale: 1.1,
              rotate: 5,
              transition: { duration: 0.3, type: 'spring', stiffness: 400 },
            }}
            animate={
              isHovered
                ? {
                    scale: [1, 1.1, 1],
                    transition: {
                      duration: 0.6,
                      repeat: Infinity,
                      repeatDelay: 2,
                    },
                  }
                : {}
            }
          >
            <div className="absolute inset-0 rounded-xl bg-black/20"></div>
            <motion.div
              className="relative z-10 text-white drop-shadow-2xl"
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.6 }}
            >
              {icon}
            </motion.div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: index * 0.1 + 0.4 }}
          >
            <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors duration-300">
              {title}
            </div>
          </motion.div>
        </motion.div>

        <motion.p
          className="text-muted-foreground mb-6 leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, delay: index * 0.1 + 0.5 }}
        >
          {description}
        </motion.p>

        <motion.div
          className="mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, delay: index * 0.1 + 0.6 }}
        >
          <h4 className="mb-3 text-sm font-semibold text-white">
            Technologies
          </h4>
          <div className="flex flex-wrap gap-2">
            {technologies.map((tech, techIndex) => (
              <motion.span
                key={techIndex}
                className="from-secondary/10 border-border/50 cursor-pointer rounded-full border bg-gradient-to-r to-blue-500/10 px-3 py-1 text-xs font-medium text-[#a273f2]"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{
                  duration: 0.3,
                  delay: index * 0.1 + 0.7 + techIndex * 0.05,
                }}
                whileHover={{
                  scale: 1.15,
                  y: -3,
                  boxShadow: '0 10px 25px -5px rgba(139, 92, 246, 0.3)',
                  transition: { duration: 0.2, type: 'spring', stiffness: 400 },
                }}
                whileTap={{ scale: 0.95 }}
              >
                {tech}
              </motion.span>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, delay: index * 0.1 + 0.8 }}
        >
          <h4 className="mb-3 text-sm font-semibold text-white">
            Key Achievements
          </h4>
          <div className="space-y-2">
            {achievements.map((achievement, achievementIndex) => (
              <motion.div
                key={achievementIndex}
                className="flex items-start gap-2 text-sm"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{
                  duration: 0.4,
                  delay: index * 0.1 + 0.9 + achievementIndex * 0.1,
                }}
              >
                <motion.div
                  className={`mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gradient-to-r ${gradientFrom} ${gradientTo} ${isHovered ? 'animate-pulse' : ''}`}
                  animate={
                    isHovered
                      ? {
                          scale: [1, 1.5, 1],
                          transition: {
                            duration: 0.6,
                            repeat: Infinity,
                            repeatDelay: 1,
                          },
                        }
                      : {}
                  }
                ></motion.div>
                <span className="text-muted-foreground leading-relaxed">
                  {achievement}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${hoverGradientFrom} ${hoverGradientTo} opacity-0`}
          animate={{
            opacity: isHovered ? 0.05 : 0,
            scale: isHovered ? 1.05 : 1,
          }}
          transition={{ duration: 0.5 }}
        ></motion.div>
      </motion.div>
    </motion.div>
  );
};

// Extracted Contact Section Component
const ContactSection = () => (
  <motion.div
    className="w-full scroll-mt-20 py-12"
    id="contact"
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.8, delay: 0.2 }}
  >
    <div className="mx-auto max-w-4xl px-4">
      <motion.div
        className="from-secondary/10 to-primary/10 border-border/50 rounded-3xl border bg-gradient-to-br p-8"
        whileHover={{
          scale: 1.02,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          transition: { duration: 0.3 },
        }}
      >
        <div className="text-center">
          <motion.h2
            className="text-foreground mb-6 text-3xl font-bold md:text-4xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Ready to Build Something{' '}
            <span className="text-primary">Amazing?</span>
          </motion.h2>
          <motion.p
            className="text-muted-foreground mx-auto mb-8 text-lg leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Let&apos;s discuss how these cutting-edge AI technologies can
            transform your business and create innovative solutions.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Link href="/contact">
              <motion.button
                className="from-primary text-primary-foreground cursor-pointer rounded-xl bg-gradient-to-r to-blue-500 px-8 py-3 font-semibold"
                whileHover={{
                  scale: 1.05,
                  boxShadow: '0 20px 40px -10px rgba(139, 92, 246, 0.4)',
                  transition: { duration: 0.3 },
                }}
                whileTap={{ scale: 0.95 }}
                aria-label="Contact me to collaborate on AI projects"
              >
                Let&apos;s Collaborate
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </div>
  </motion.div>
);

// Main AI Component
export default function Ai() {
  // Memoized event handlers

  // Memoized AI cards data
  const aiCardsData = useMemo(
    () => [
      {
        title: 'Large Language Models',
        description:
          'Advanced fine-tuning and deployment of state-of-the-art language models',
        technologies: [
          'GPT-4',
          'Claude',
          'Llama 2/3',
          'Mistral',
          'Gemini',
          'LoRA',
          'QLoRA',
          'PEFT',
        ],
        achievements: [
          'Fine-tuned 15+ custom LLMs for domain-specific tasks',
          'Implemented LoRA/QLoRA for efficient parameter updates',
          'Deployed production-ready inference endpoints',
        ],
        icon: <Cpu className="h-6 w-6" />,
        gradientFrom: 'from-blue-500',
        gradientTo: 'to-cyan-500',
        hoverGradientFrom: 'from-blue-500',
        hoverGradientTo: 'to-cyan-500',
      },
      {
        title: 'Transformer Architecture',
        description:
          'Deep understanding of attention mechanisms and neural network architectures',
        technologies: [
          'BERT',
          'T5',
          'RoBERTa',
          'DeBERTa',
          'Vision Transformers',
          'CLIP',
          'DALL-E',
          'Stable Diffusion',
        ],
        achievements: [
          'Built custom transformer models from scratch',
          'Optimized attention mechanisms for efficiency',
          'Implemented multi-modal transformer architectures',
        ],
        icon: <Server className="h-6 w-6" />,
        gradientFrom: 'from-pink-400',
        gradientTo: 'to-rose-500',
        hoverGradientFrom: 'from-purple-500',
        hoverGradientTo: 'to-pink-500',
      },
      {
        title: 'Generative AI & GANs',
        description:
          'Creating synthetic data and generative models for creative applications',
        technologies: [
          'StyleGAN',
          'CycleGAN',
          'WGAN',
          'VAE',
          'Diffusion Models',
          'CLIP',
          'ControlNet',
          'InstructPix2Pix',
        ],
        achievements: [
          'Developed custom GAN architectures for image synthesis',
          'Implemented diffusion models for high-quality generation',
          'Created AI-powered content generation pipelines',
        ],
        icon: <Zap className="h-6 w-6" />,
        gradientFrom: 'from-green-500',
        gradientTo: 'to-emerald-500',
        hoverGradientFrom: 'from-green-500',
        hoverGradientTo: 'to-emerald-500',
      },
      {
        title: 'MLOps & Model Deployment',
        description:
          'End-to-end ML pipeline management and scalable model deployment',
        technologies: [
          'Docker',
          'Kubernetes',
          'MLflow',
          'Weights & Biases',
          'Hugging Face',
          'TensorRT',
          'ONNX',
          'FastAPI',
        ],
        achievements: [
          'Deployed 25+ models to production environments',
          'Built automated ML training and deployment pipelines',
          'Optimized model inference for sub-100ms latency',
        ],
        icon: <Database className="h-6 w-6" />,
        gradientFrom: 'from-purple-500',
        gradientTo: 'to-indigo-500',
        hoverGradientFrom: 'from-purple-500',
        hoverGradientTo: 'to-indigo-500',
      },
      {
        title: 'Vector Databases & RAG',
        description:
          'Advanced retrieval-augmented generation and semantic search systems',
        technologies: [
          'Pinecone',
          'Weaviate',
          'Chroma',
          'FAISS',
          'LangChain',
          'LlamaIndex',
          'OpenAI Embeddings',
          'Sentence Transformers',
        ],
        achievements: [
          'Built enterprise RAG systems handling 10M+ documents',
          'Implemented hybrid search with semantic + keyword matching',
          'Optimized embedding models for domain-specific retrieval',
        ],
        icon: <VectorSquare className="h-6 w-6" />,
        gradientFrom: 'from-orange-500',
        gradientTo: 'to-red-500',
        hoverGradientFrom: 'from-orange-500',
        hoverGradientTo: 'to-red-500',
      },
      {
        title: 'AI Agent Systems',
        description: 'Multi-agent architectures and autonomous AI systems',
        technologies: [
          'LangChain',
          'CrewAI',
          'AutoGEN',
          'ReAct',
          'Function Calling',
          'Tool Use',
          'Multi-Agent Systems',
          'Planning Algorithms',
        ],
        achievements: [
          'Developed autonomous AI agents for complex workflows',
          'Implemented multi-agent collaboration systems',
          'Built tool-using agents with 95%+ task completion rate',
        ],
        icon: <Workflow className="h-6 w-6" />,
        gradientFrom: 'from-teal-500',
        gradientTo: 'to-cyan-500',
        hoverGradientFrom: 'from-teal-500',
        hoverGradientTo: 'to-cyan-500',
      },
    ],
    [],
  );

  return (
    <motion.div
      className="relative"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <div className="w-full">
        <div className="min-h-screen">
          <div className="py-20">
            <section
              className="relative scroll-mt-20 rounded-3xl py-20"
              id="expertise"
              style={{
                transform: 'translateZ(0)',
                backfaceVisibility: 'hidden',
                perspective: '1000px',
              }}
            >
              {/* Floating background elements */}
              <motion.div
                className="absolute inset-0 overflow-hidden"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 1 }}
              ></motion.div>

              <div className="absolute inset-0"></div>
              <div
                className="relative z-10 mx-auto max-w-7xl px-4"
                style={{ willChange: 'auto' }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="mb-16 text-center"
                >
                  <h2 className="bricolage mb-6 text-4xl font-extrabold md:text-5xl">
                    AI &amp; ML <span className="text-primary">Expertise</span>
                  </h2>
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="antonio text-muted-foreground mx-auto max-w-4xl text-xl sm:text-2xl"
                  >
                    Specialized in cutting-edge AI technologies and modern
                    machine learning architectures
                  </motion.p>
                </motion.div>

                <motion.div
                  className="grid gap-8 md:grid-cols-2 lg:grid-cols-3"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                >
                  {aiCardsData.map((card, index) => (
                    <AICard key={index} {...card} index={index} />
                  ))}
                </motion.div>
              </div>
            </section>
            <ContactSection />
          </div>
        </div>
      </div>
    </motion.div>
  );
}
